import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import MemberForm, { IMemberFormData } from '../../components/forms/MemberForm';
import MyMembershipsPanel from '../../components/MyMembershipsPanel';
import { translateError } from '../../utils/errorMessages';

interface IGroupDisplay {
  _id: string;
  name: string;
}

interface IMember {
  _id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  description?: string;
  groups?: IGroupDisplay[] | string[];
  dateOfBirth?: string;
  nationality?: string;
  birthNumber?: string;
  email?: string;
  phone?: string;
  allergies?: string;
  medicalNotes?: string;
  /** Backend stores the address as a jsonb object. */
  address?: {
    city?: string;
    cityDistrict?: string;
    street?: string;
    zipCode?: string;
    buildingNumber?: string;
    streetNumber?: string;
  } | null;
  /** Backend stores the emergency contact as a jsonb object. */
  emergencyContact?: {
    name?: string;
    phone?: string;
    relationship?: string;
  } | null;
}

type MemberTab = 'details' | 'memberships';

const MemberDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [member, setMember] = useState<IMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeTab: MemberTab = searchParams.get('tab') === 'memberships' ? 'memberships' : 'details';
  const setActiveTab = (tab: MemberTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const fetchMember = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const { data } = await api.my.getMember(id);
      setMember(data as IMember);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
      if (err.response && err.response.status === 404) {
        navigate('/dashboard/members');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchMember();
  }, [fetchMember]);

  const submitHandler = async (data: IMemberFormData) => {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      await api.my.updateMember(id, data);
      addToast(t('members.editTitle') + ' - ' + t('common.save') + '!', 'success');
      await fetchMember();
    } catch (err: any) {
      const code = err.response?.data?.code;
      const errorMessage =
        code === 'DUPLICATE_BIRTH_NUMBER'
          ? t('members.enterBirthNumber')
          : translateError(err, t);
      setError(errorMessage);
      addToast(t('common.error') + ': ' + errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !member) return <p>{t('members.loadingMember')}</p>;
  if (error && !member) return <p className="error-message">{t('common.error')}: {error}</p>;

  // Unfold the jsonb address into the form's flat fields (mirrors MemberEditPage).
  const initialData = member
    ? {
        name: member.name,
        firstName: member.firstName,
        lastName: member.lastName,
        description: member.description ?? '',
        groups: (member.groups || []).map((g: any) => (typeof g === 'string' ? g : g._id)),
        dateOfBirth: member.dateOfBirth ? member.dateOfBirth.split('T')[0] : undefined,
        city: member.address?.city,
        cityDistrict: member.address?.cityDistrict,
        nationality: member.nationality,
        birthNumber: member.birthNumber,
        street: member.address?.street,
        zipCode: member.address?.zipCode,
        buildingNumber: member.address?.buildingNumber,
        streetNumber: member.address?.streetNumber,
        email: member.email,
        phone: member.phone,
        allergies: member.allergies,
        medicalNotes: member.medicalNotes,
        address: member.address ?? null,
        // MemberForm always submits this; not seeding it wrote null over a
        // stored emergency contact on every save.
        emergencyContact: member.emergencyContact ?? null,
      }
    : undefined;

  return (
    <div className="admin-form-page">
      <h2>{t('members.detailTitle')}{member ? `: ${member.name}` : ''}</h2>

      <div className="tab-switcher" style={{ marginBottom: '1.5rem' }}>
        <button
          className={activeTab === 'details' ? 'active' : ''}
          onClick={() => setActiveTab('details')}
          type="button"
        >
          {t('members.tabDetails')}
        </button>
        <button
          className={activeTab === 'memberships' ? 'active' : ''}
          onClick={() => setActiveTab('memberships')}
          type="button"
        >
          {t('members.tabMemberships')}
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="tab-content active form-section">
          {error && <p className="error-message">{t('common.error')}: {error}</p>}
          <MemberForm
            initialData={initialData}
            seedKey={member?._id}
            onSubmit={submitHandler}
            loading={saving}
            submitButtonText={t('members.updateButton')}
            isUserView={true}
          />
        </div>
      )}

      {activeTab === 'memberships' && id && (
        <div className="tab-content active">
          <MyMembershipsPanel memberId={Number(id)} />
        </div>
      )}

      <Link to="/dashboard/members" className="form-link">{t('members.goBack')}</Link>
    </div>
  );
};

export default MemberDetailPage;
